import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'
import { User, AuthResponse } from '../services/api'

interface AuthState {
  isAuthenticated: boolean
  isLoading: boolean
  user: User | null
  token: string | null
  refreshToken: string | null
  scopes: Array<{ scopeType: string; scopeId: string | null }>

  // Actions
  login: (authData: AuthResponse) => void
  logout: () => void
  setLoading: (loading: boolean) => void
  updateUser: (userData: Partial<User>) => void
  setScopes: (scopes: Array<{ scopeType: string; scopeId: string | null }>) => void
  hasScope: (scopeType: string) => boolean
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      isAuthenticated: false,
      isLoading: false,
      user: null,
      token: null,
      refreshToken: null,
      scopes: [],

      login: (authData: AuthResponse) => {
        set({
          isAuthenticated: true,
          user: authData.user,
          token: authData.token,
          refreshToken: authData.refreshToken,
          isLoading: false,
        })
      },

      logout: () => {
        set({
          isAuthenticated: false,
          user: null,
          token: null,
          refreshToken: null,
          scopes: [],
          isLoading: false,
        })
      },

      setLoading: (loading: boolean) => {
        set({ isLoading: loading })
      },

      updateUser: (userData: Partial<User>) => {
        const currentUser = get().user
        if (currentUser) {
          set({
            user: { ...currentUser, ...userData },
          })
        }
      },

      setScopes: (scopes: Array<{ scopeType: string; scopeId: string | null }>) => {
        set({ scopes })
      },

      hasScope: (scopeType: string) => {
        return get().scopes.some(s => s.scopeType === scopeType)
      },
    }),
    {
      name: 'ecombine-web-auth',
      storage: createJSONStorage(() => localStorage),
      partialize: state => ({
        isAuthenticated: state.isAuthenticated,
        user: state.user,
        token: state.token,
        refreshToken: state.refreshToken,
        scopes: state.scopes,
      }),
    }
  )
)
